import { Link } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { useNdfMoi } from '../hooks/useNdfMoi';
import {
  buildModules,
  buildSections,
  navigationParSections,
  type ModuleDef,
} from '../components/layout/modules';
import { useCompteurs, type Compteurs } from '../hooks/useCompteurs';
import { useApercu } from '../components/ndf/ui/useApercu';
import { BarreApercu } from '../components/ndf/ui/BarreApercu';

/**
 * Page d'accueil — les modules en cartes, avec ce qui attend la personne.
 *
 * Le menu latéral disparaît ici : ces cartes SONT la navigation. Elles ne se
 * contentent pas de mener quelque part, elles disent s'il faut y aller — un
 * lanceur sans chiffres ne serait qu'un menu plus lent.
 *
 * Les modules viennent de `buildModules`, la même source que le menu : ajouter
 * un module l'ajoutera aux deux, ou à aucun.
 */

/** Une ligne de carte : un chiffre et ce qu'il veut dire. */
interface Indication {
  n: number;
  texte: string;
  /** Ce qui appelle une action de ma part se voit ; le reste informe. */
  ton: 'action' | 'info';
}

/** Accord en nombre, pour ne pas écrire « 1 factures ». */
function pluriel(n: number, singulier: string, pluriel: string): string {
  return n > 1 ? pluriel : singulier;
}

/**
 * Ce qu'une carte doit annoncer.
 *
 * Rien à signaler = aucune ligne, et la carte reste sobre. Afficher « 0 note à
 * approuver » ferait chercher ce qui n'existe pas.
 */
function indications(cle: string, c: Compteurs): Indication[] {
  const lignes: Indication[] = [];

  if (cle === 'factures' && c.factures) {
    if (c.factures.a_valider > 0) {
      lignes.push({
        n: c.factures.a_valider,
        texte: `${pluriel(c.factures.a_valider, 'facture', 'factures')} à valider`,
        ton: 'action',
      });
    }
    if (c.factures.en_erreur > 0) {
      lignes.push({
        n: c.factures.en_erreur,
        texte: `en erreur d’extraction`,
        ton: 'action',
      });
    }
  }

  if (cle === 'paiements' && c.paiements) {
    const p = c.paiements;
    if (p.a_valider) lignes.push({ n: p.a_valider, texte: 'en attente de validation', ton: 'action' });
    if (p.a_preparer) lignes.push({ n: p.a_preparer, texte: 'à préparer', ton: 'info' });
    if (p.a_exporter) lignes.push({ n: p.a_exporter, texte: 'prêts à exporter', ton: 'action' });
  }

  if (cle === 'notes-de-frais' && c.ndf) {
    const n = c.ndf;
    if (n.a_approuver) {
      lignes.push({
        n: n.a_approuver,
        texte: `${pluriel(n.a_approuver, 'note', 'notes')} à approuver`,
        ton: 'action',
      });
    }
    if (n.mes_notes_refusees) {
      lignes.push({
        n: n.mes_notes_refusees,
        texte: `${pluriel(n.mes_notes_refusees, 'note refusée', 'notes refusées')} à corriger`,
        ton: 'action',
      });
    }
    if (n.mes_depenses_libres) {
      lignes.push({
        n: n.mes_depenses_libres,
        texte: `${pluriel(n.mes_depenses_libres, 'dépense', 'dépenses')} pas encore dans une note`,
        ton: 'info',
      });
    }
    if (n.mes_notes_en_cours) {
      lignes.push({
        n: n.mes_notes_en_cours,
        texte: `${pluriel(n.mes_notes_en_cours, 'note', 'notes')} en cours de traitement`,
        ton: 'info',
      });
    }
  }

  if (cle === 'support' && c.tickets?.a_traiter) {
    lignes.push({
      n: c.tickets.a_traiter,
      texte: `${pluriel(c.tickets.a_traiter, 'alerte', 'alertes')} à traiter`,
      ton: 'action',
    });
  }

  return lignes;
}

export function AccueilPage() {
  const { user, logout } = useUser();
  const ndf = useNdfMoi();
  // En aperçu de rôle, les cartes sont celles de la personne ENDOSSÉE : ses
  // droits `ndf` arrivent déjà projetés, et les modules qui dépendent du compte
  // réel (factures, paiements) sont neutralisés — sans quoi on lui prêterait
  // l'écran des factures fournisseurs de l'observateur.
  const enApercu = Boolean(useApercu());
  const { data: compteurs } = useCompteurs();

  const estAdmin = user?.role === 'admin' && !enApercu;
  const estValideur = Boolean(user?.is_payment_validator) && !enApercu;
  const modules = buildModules(estAdmin, estValideur, ndf);
  // L'accueil est le MIROIR du menu : mêmes sections, même rangement. Deux
  // organisations différentes pour les mêmes modules obligeraient à réapprendre
  // en passant de l'un à l'autre.
  const sections = navigationParSections(estAdmin, ndf)
    ? buildSections(estAdmin, estValideur, ndf)
    : null;

  const prenom = (user?.name ?? user?.email ?? '').split(/[ .@]/)[0];
  const bonjour = prenom ? `Bonjour ${prenom.charAt(0).toUpperCase()}${prenom.slice(1)}` : 'Bonjour';

  return (
    <div className="space-y-8">
      {/* Le bandeau vit AUSSI ici : on atterrit sur l'accueil en changeant
          d'identité, et sans lui il n'y aurait, depuis cet écran, aucun moyen
          de savoir chez qui l'on est ni de revenir à son propre compte. */}
      {enApercu && <BarreApercu />}

      {/* BAC À SABLE — pré-production ET administrateur, jamais en production.
          Il vivait uniquement dans un onglet du module Notes de frais, où l'on
          ne passe plus : l'application s'ouvre désormais sur cet accueil, et le
          menu latéral y est masqué. Un outil de recette qu'il faut chercher
          dans un autre module n'est pas un outil de recette.
          Le rôle vient de la SESSION, donc du compte réel : c'est la traduction
          visible d'une règle que le serveur tient déjà — il n'honore l'en-tête
          d'aperçu que pour un administrateur. */}
      {ndf.bacASable && user?.role === 'admin' && !enApercu && (
        <section className="rounded-lg border border-violet-fond/60 border-l-4 border-l-violet-fond bg-white p-4">
          <h2 className="mb-1 font-titre text-sm font-bold text-nuit">Bac à sable</h2>
          <p className="mb-3 text-sm text-nuit/70">
            Environnement de test : utilisez l’application à la place d’une autre personne pour
            dérouler un circuit complet sans changer de compte Google.
          </p>
          <BarreApercu />
        </section>
      )}

      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-titre text-2xl font-bold text-nuit">{bonjour}</h1>
          <p className="text-sm text-nuit/60">Où souhaitez-vous aller ?</p>
        </div>
        <div className="text-right">
          <p className="text-sm text-nuit/60">{user?.email}</p>
          <button
            type="button"
            onClick={logout}
            className="text-sm font-medium text-corail hover:underline"
          >
            Se déconnecter
          </button>
        </div>
      </header>

      {sections ? (
        sections.map((s) => (
          <section key={s.cle} className="space-y-3">
            <h2 className="flex items-center gap-2 font-titre text-sm font-bold uppercase tracking-wide text-nuit/50">
              <span aria-hidden>{s.icone}</span>
              {s.libelle}
            </h2>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {s.modules.map((m) => (
                <Carte key={m.key} module={m} lignes={indications(m.key, compteurs ?? {})} />
              ))}
            </div>
          </section>
        ))
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {modules.map((m) => (
            <Carte key={m.key} module={m} lignes={indications(m.key, compteurs ?? {})} />
          ))}
        </div>
      )}
    </div>
  );
}

function Carte({ module, lignes }: { module: ModuleDef; lignes: Indication[] }) {
  const aDuTravail = lignes.some((l) => l.ton === 'action');

  return (
    <Link
      to={module.to}
      className={`group flex min-h-44 flex-col rounded-xl border bg-white p-5 transition-shadow hover:shadow-md ${
        aDuTravail ? 'border-l-4 border-l-jaune-fond border-nuit/10' : 'border-nuit/10'
      }`}
    >
      <div className="flex items-start gap-3">
        <span className="text-2xl leading-none" aria-hidden>
          {module.icon}
        </span>
        <div className="min-w-0">
          <h2 className="font-titre font-bold text-nuit group-hover:underline">{module.label}</h2>
          <p className="mt-0.5 text-sm text-nuit/60">{module.description}</p>
        </div>
      </div>

      <div className="mt-4 flex-1 space-y-1">
        {lignes.length === 0 ? (
          <p className="text-sm text-nuit/40">Rien en attente.</p>
        ) : (
          lignes.map((l) => (
            <p key={l.texte} className="text-sm">
              <span
                className={`mr-1.5 inline-block min-w-6 rounded px-1.5 text-center font-titre font-bold ${
                  l.ton === 'action' ? 'bg-jaune-fond text-nuit' : 'bg-nuit/5 text-nuit/70'
                }`}
              >
                {l.n}
              </span>
              <span className={l.ton === 'action' ? 'text-nuit' : 'text-nuit/60'}>{l.texte}</span>
            </p>
          ))
        )}
      </div>
    </Link>
  );
}
